import { objectPosition } from "3d/constants/common";
import { NodeComponentType, TubeType, } from "store/scene/types.current";
/*
 * The suction cups of a real tool are bolted to a frame — a "grimma" — and the
 * whole thing is moved as one piece. This module works out where that frame
 * sits for the tool currently drawn in the scene.
 *
 * The frame is *derived*, never stored: it is the bounding box of the cups plus
 * clearance. So there is no second source of truth to keep in step — add, move
 * or remove a cup and the frame simply follows.
 */
/** Name of the frame's root entity, which is what the picker grabs. */
export var GRIP_FRAME_ENTITY_NAME = "Grip Frame";
/** Clearance (metres) between the outermost cup edge and the frame's edge.
 * Generous on purpose: it keeps the corner handle clear of the outermost
 * cups, so grabbing a cup never fights the frame's drag icon. */
export var gripFrameClearance = 0.1;
/*
 * The frame is drawn as an understated translucent plate with a drag icon in
 * its upper-left corner (upper-left as seen in the top-down view: -X is screen
 * left, -Z is screen up). The plate is deliberately visual-only — it covers
 * the cups, so it must never swallow their picks (see the builder, which
 * excludes it from the picker) — and the handle carrying the icon is the one
 * part of the frame the picker grabs.
 */
export var gripFramePlateThickness = 0.004;
/** Side (metres) of the square drag handle the move icon is drawn on. */
export var gripFrameHandleSize = 0.08;
/* The handle is a flat icon, not a block: it just floats a hair above the
 * plate so the two never z-fight. */
export var gripFrameHandleLift = 0.002;
/*
 * Height of the frame. Above the cups (which is where a tool's frame belongs)
 * but below the tube network at `tubeNetworkY`, so in the top-down view the
 * tubes are drawn crossing over the frame rather than hidden by it.
 */
export var gripFrameY = objectPosition.y + 0.25;
/*
 * A frame around a single cup is just a box around that cup, and there is
 * nothing to move together — so one cup gets no frame.
 */
export var minGripPositionsForFrame = 2;
var isGripPosition = function (node) {
    return (node === null || node === void 0 ? void 0 : node.type) === NodeComponentType.GRIP_POSITION;
};
/*
 * The frame enclosing the scene's suction cups, or `null` when there are too
 * few cups to frame.
 */
export var computeGripFrame = function (nodes) {
    var gripPositions = Object.values(nodes).filter(isGripPosition);
    if (gripPositions.length < minGripPositionsForFrame)
        return null;
    var minX = Infinity;
    var maxX = -Infinity;
    var minZ = Infinity;
    var maxZ = -Infinity;
    gripPositions.forEach(function (node) {
        var _a;
        var cup = node.component;
        /* The cup's largest half-extent, whichever way it is turned: an oval cup's
         * long side may lie along either axis, and the frame only has to enclose
         * it — a few millimetres of slack costs nothing here. */
        var reach = Math.max(cup.width, (_a = cup.height) !== null && _a !== void 0 ? _a : cup.width) / 2;
        minX = Math.min(minX, node.position.x - reach);
        maxX = Math.max(maxX, node.position.x + reach);
        minZ = Math.min(minZ, node.position.z - reach);
        maxZ = Math.max(maxZ, node.position.z + reach);
    });
    return {
        center: { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 },
        length: maxX - minX + 2 * gripFrameClearance,
        width: maxZ - minZ + 2 * gripFrameClearance,
    };
};
/*
 * What is mounted on the frame, and so travels when it is moved or turned:
 * every grip position (with whatever it holds — cup, mounting, decentral
 * pump), plus every tube connection joined straight to a cup by a vacuum tube,
 * the way a manifold sits on the tool right behind its cups.
 *
 * Everything else stays where it is: the feed pressure point, central pumps,
 * tube connections on the compressed-air side and those further back towards
 * the pumps. The tubes between what moves and what stays are re-measured after
 * the move, like after any other node move, so the calculation can change.
 *
 * Only the cups decide where the frame is (see `computeGripFrame`); a tube
 * connection is carried along but does not widen the frame.
 */
export var getNodesMountedOnGripFrame = function (_a) {
    var nodes = _a.nodes, edges = _a.edges, attachments = _a.attachments;
    var nodeAt = function (attachmentId) { var _a; return nodes[(_a = attachments[attachmentId]) === null || _a === void 0 ? void 0 : _a.nodeId]; };
    var mounted = Object.values(nodes).filter(isGripPosition);
    var mountedIds = new Set(mounted.map(function (node) { return node.id; }));
    Object.values(edges).forEach(function (edge) {
        if (edge.tubeType !== TubeType.VACUUM)
            return;
        var ends = [nodeAt(edge.fromAttachmentId), nodeAt(edge.toAttachmentId)];
        if (!ends.some(isGripPosition))
            return;
        ends.forEach(function (node) {
            if ((node === null || node === void 0 ? void 0 : node.type) === NodeComponentType.TUBE_CONNECTION &&
                !mountedIds.has(node.id)) {
                mountedIds.add(node.id);
                mounted.push(node);
            }
        });
    });
    return mounted;
};
/*
 * The frame turns a quarter of a turn and back again, never further: a tool
 * turned twice the same way would face backwards, which is not what the turn
 * is for (lining the cups up with a layer whose boxes lie the other way).
 */
export var gripFrameTurnDirection = function (isTurned) { return (isTurned ? "counterClockwise" : "clockwise"); };
/*
 * Change in a node's `rotationY` (degrees) for a quarter turn. Clockwise as
 * seen in the top-down view is a negative turn about +Y — the same sign the
 * node toolbar's own rotate-clockwise button uses.
 */
export var gripFrameTurnDegrees = function (direction) {
    return direction === "clockwise" ? -90 : 90;
};
/*
 * Where a point ends up after a quarter turn of the frame about `centre`. In
 * the top-down view -Z is screen up, so turning clockwise takes +X (right) to
 * +Z (down) and +Z to -X.
 */
export var turnAboutGripFrameCentre = function (point, centre, direction) {
    var dx = point.x - centre.x;
    var dz = point.z - centre.z;
    return direction === "clockwise"
        ? { x: centre.x - dz, z: centre.z + dx }
        : { x: centre.x + dz, z: centre.z - dx };
};
/*
 * The translucent plate covering the frame's whole footprint. Centred on the
 * root, so it needs no offset.
 */
export var buildGripFramePlate = function (_a) {
    var length = _a.length, width = _a.width;
    return ({
        scale: [length, gripFramePlateThickness, width],
        position: [0, 0, 0],
    });
};
/*
 * The drag handle, a flat square lying on the plate in its upper-left corner
 * (-X/-Z) — the builder draws the move icon onto it. Never larger than the
 * plate itself, so the frame around a small tool doesn't degenerate into all
 * handle.
 */
export var buildGripFrameHandle = function (_a) {
    var length = _a.length, width = _a.width;
    var side = Math.min(gripFrameHandleSize, length, width);
    return {
        scale: [side, 1, side],
        position: [
            -(length - side) / 2,
            gripFramePlateThickness / 2 + gripFrameHandleLift,
            -(width - side) / 2,
        ],
    };
};
